import * as React from 'react';

import type { W_Account_FindAllForReportTypeView_VM_User } from 'ab-api/web/account/findAllForReportTypeView/viewModel';
import type { W_ReportType_FindAllReportBlockFieldsForFilter_VM_Single } from 'ab-api/web/reportType/findAllReportBlockFieldsForFilter';

import { ReportTypeViewTableAdditionalFilterType, type ReportTypeViewTableAdditionalFilter } from '../types';
import FieldFilter from './FieldFilter';
import JobFilter from './JobFilter';
import styles from './styles.module.scss';
import type { JobFilterItem } from '../JobFilterItem';

type Props = {
	filters: ReportTypeViewTableAdditionalFilter[];
	editFilter: (id: string, newValue: ReportTypeViewTableAdditionalFilter) => void;
	resetAdditionalFilters: () => void;
	removeFilter: (id: string) => void;
	accountOptions: W_Account_FindAllForReportTypeView_VM_User[];
	equipmentOptions: {
		id: number;
		code: string;
		specification: Nullable<string>;
	}[];
	jobOptions: JobFilterItem[];
	lazyLoadAccountOptions: (isLoaded: boolean) => Promise<void>;
	lazyLoadEquipmentOptions: (isLoaded: boolean) => Promise<void>;
	lazyLoadJobOptions: (isLoaded: boolean) => Promise<void>;
	reportBlockFieldOptions: W_ReportType_FindAllReportBlockFieldsForFilter_VM_Single[];
	lazyLoadReportBlockFieldsOptions: (loaded: boolean) => Promise<void>;
};

const AdditionalFilters: React.FC<Props> = (props) => {
	const {
		filters,
		editFilter,
		resetAdditionalFilters,
		removeFilter,
		accountOptions,
		equipmentOptions,
		lazyLoadAccountOptions,
		lazyLoadEquipmentOptions,
		jobOptions,
		lazyLoadJobOptions,
		lazyLoadReportBlockFieldsOptions,
		reportBlockFieldOptions,
	} = props;

	const filterMapper = React.useCallback((_filter: ReportTypeViewTableAdditionalFilter) => {
		const _initiallyOpened = _filter.value === null;
		if (_filter.type === ReportTypeViewTableAdditionalFilterType.JOB) {
			return (
				<JobFilter
					editFilter={editFilter}
					filter={_filter}
					initiallyOpened={_initiallyOpened}
					jobOptions={jobOptions}
					key={_filter.id}
					lazyLoadJobs={lazyLoadJobOptions}
					removeFilter={removeFilter}
				/>
			);
		} else if (_filter.type === ReportTypeViewTableAdditionalFilterType.FIELD) {
			return (
				<FieldFilter
					accountOptions={accountOptions}
					editFilter={editFilter}
					equipmentOptions={equipmentOptions}
					filter={_filter}
					initiallyOpened={_initiallyOpened}
					key={_filter.id}
					lazyLoadAccountOptions={lazyLoadAccountOptions}
					lazyLoadEquipmentOptions={lazyLoadEquipmentOptions}
					lazyLoadReportBlockFieldOptions={lazyLoadReportBlockFieldsOptions}
					removeFilter={removeFilter}
					reportBlockFieldOptions={reportBlockFieldOptions}
				/>
			);
		}
		return null;
	},
		[
			accountOptions,
			editFilter,
			equipmentOptions,
			jobOptions,
			lazyLoadAccountOptions,
			lazyLoadEquipmentOptions,
			lazyLoadJobOptions,
			lazyLoadReportBlockFieldsOptions,
			removeFilter,
			reportBlockFieldOptions,
		]);

	return (
		<div className={styles['additional-filters']}>
			{filters.map(filterMapper)}
			{filters.length
				? <span className={styles['additional-filters__reset-button']} onClick={resetAdditionalFilters}>
					Reset
				</span>
				: null
			}
		</div>
	);
};

export default React.memo(AdditionalFilters);
